import React, { useState, useEffect, useMemo } from 'react'
import './QueryAnalysis.css'
import LoadingIndicator from './LoadingIndicator'
import MetricsDisplay from './MetricsDisplay'
import QuestionsTableSlim from './QuestionsTableSlim'
import ErrorText from './ErrorText'
import ConversationPopup from './ConversationPopup'
import BottomRightButton from './BottomRightButton'
import SourcesTable from './SourcesTable'
import { getEnv } from '../config/env'

// Map a chat_turns doc returned by /analyze_external_v2 into the shape
// expected by QuestionsTableSlim / ConversationPopup.
const mapChatTurnToPoint = (doc) => {
  const classification = doc.classification || {}
  const answer = doc.answer || {}
  return {
    id: doc._id,
    parent_id: doc.parent_id,
    session_id: doc.session_id,
    tenant_id: doc.tenant_id,
    query: doc.question || '',
    answer: answer.content || '',
    sources: answer.sources || [],
    selected_sources: [],
    used_indexes: classification.indexes_used || [],
    feedback: doc.feedback ?? 0,
    date: doc.timestamp,
    language: doc.language_code || doc.language,
    eval_data: doc.metrics || {},
    // New fields surfaced from chat_turns:
    skills_activated: classification.skills_activated || [],
    tool_names_used: classification.tool_names_used || [],
    mcp_servers_used: classification.mcp_servers_used || [],
    had_rag: !!classification.had_rag,
    had_approval: !!classification.had_approval,
    had_error: !!classification.had_error,
    orchestrator_path: doc.orchestrator_path,
    timings: doc.timings || {}
  }
}

const QueryAnalysis = () => {
  const [loading, setLoading] = useState(false)
  const [dates, setDates] = useState([])
  const [selectedFeedback, setSelectedFeedback] = useState([])
  const [textSearch, setTextSearch] = useState('')
  const [error, setError] = useState(null)
  const [selectedIndexes, setSelectedIndexes] = useState([])
  const [selectedTenant, setSelectedTenant] = useState([])
  const [tenant_dict, setTenantDict] = useState([])
  const [points, setPoints] = useState([])
  const [conversationData, setConversationData] = useState(null)
  const [conversationMetaData, setConversationMetaData] = useState(null)
  const [warning, setWarning] = useState(null)
  const [tenant_preview, setTenantPreview] = useState('Value') // tenant dropdown label

  const [metrics, setMetrics] = useState(null)
  const [selectedLanguage, setSelectedLanguage] = useState(null)
  const [sourcesCount, setSourcesCount] = useState({})
  const [approvedQueriesData, setApprovedQueriesData] = useState([])

  // ── New chat_turns filters (Phase 4) ──────────────────────────────
  const [filterOptions, setFilterOptions] = useState({
    skills: [],
    tools: [],
    mcp_servers: [],
    indexes: [],
    languages: [],
    step_types: [],
    orchestrator_paths: []
  })
  const [selectedSkills, setSelectedSkills] = useState([])
  const [selectedTools, setSelectedTools] = useState([])
  const [selectedMcpServers, setSelectedMcpServers] = useState([])
  const [selectedStepTypes, setSelectedStepTypes] = useState([])
  const [selectedOrchestratorPath, setSelectedOrchestratorPath] = useState('new')

  const [faithfulnessRange, setFaithfulnessRange] = useState([0, 1])
  const [answerRelevancyRange, setAnswerRelevancyRange] = useState([0, 1])

  const [tenant_search_value, setTenantSearchValue] = useState('')

  const handleTenantSearchChange = (event) => {
    setTenantSearchValue(event.target.value)
  }

  const deepEvalSliderValues = [
    { value: 0.0, text: '0.0 ❌', tick: false },
    { value: 0.1, text: '0.1 ❌', tick: false },
    { value: 0.2, text: '0.2 ❌', tick: false },
    { value: 0.3, text: '0.3 ❌', tick: false },
    { value: 0.4, text: '0.4 ❌', tick: false },
    { value: 0.5, text: '0.5 ❌', tick: false },
    { value: 0.6, text: '0.6 ❌', tick: false },
    { value: 0.699, text: '0.69 ❌', tick: false },
    { value: 0.7, text: '0.7 ✅', tick: true },
    { value: 0.8, text: '0.8 ✅', tick: true },
    { value: 0.9, text: '0.9 ✅', tick: true },
    { value: 1.0, text: '1.0 ✅', tick: true }
  ]

  const feedback_dict = [
    { value: '1', text: 'Positive' },
    { value: '-1', text: 'Negative' }
  ]

  async function fetchData() {
    let response = []
    try {
      response = await window.PDR.api.get(`${getEnv('REACT_APP_AI_INSIGHTS_BASE_URL')}/get_tenants`)
      return response
    } catch (error) {
      console.error('Error fetching data:', error)
    }
    return response
  }

  useEffect(() => {
    fetchData().then((response) => {
      console.log('Response:', response)

      const allOption = { value: 'All', text: 'Select/Remove All' }

      const sorted = (Array.isArray(response) ? response : [])
        // avoid duplicates of the "All" option if it’s already in the data
        .filter((item) => item && item.value !== 'All' && item.text !== allOption.text)
        .sort((a, b) => (a.text ?? '').localeCompare(b.text ?? '', undefined, { sensitivity: 'base' }))

      setTenantDict([allOption, ...sorted])
    })
  }, [])

  const handleFullConversation = async (point) => {
    setConversationMetaData(point)
    const response = await window.PDR.api.post(
      `${getEnv('REACT_APP_AI_INSIGHTS_BASE_URL')}/get_full_conversation_v2`,
      {
        session_id: point.session_id
      }
    )
    setConversationData(response)
  }

  const closePopup = () => {
    setConversationData(null)
  }

  const handleTenantChange = (event) => {
    if (event.target.value.includes('All')) {
      if (event.target.value.length === 1) {
        setSelectedTenant(tenant_dict.map((tenant) => tenant.value).filter((value) => value !== 'All'))
        setTenantPreview(
          tenant_dict.map((tenant) => tenant.value).filter((value) => value !== 'All').length + ' selected'
        )
      } else {
        setSelectedTenant([])
        setTenantPreview('Value')
      }
    } else {
      setSelectedTenant(event.target.value)

      if (event.target.value.length > 1) {
        setTenantPreview(event.target.value.length + ' selected')
      } else if (event.target.value.length === 1) {
        setTenantPreview(tenant_dict.find((tenant) => tenant.value === event.target.value[0])?.text || '')
      } else {
        setTenantPreview('Value')
      }
    }
  }

  const handleTextSearchChange = (event) => {
    setTextSearch(event.target.value)
  }

  const handleFeedbackChange = (event) => {
    setSelectedFeedback(event.target.value)
  }

  const handleIndexChange = (event) => {
    setSelectedIndexes(event.target.value)
  }

  const renderError = () => {
    return <ErrorText message={error} />
  }

  const handleChangeDate = (event) => {
    if (event.target.value.length < 1) {
      setDates([])
    } else {
      setDates(event.target.value)
    }
  }

  const handleFaithfulnessChange = (e) => {
    setFaithfulnessRange(e.target.value)
  }
  const handleRelevancyChange = (e) => {
    setAnswerRelevancyRange(e.target.value)
  }

  const handleSkillsChange = (event) => setSelectedSkills(event.target.value)
  const handleToolsChange = (event) => setSelectedTools(event.target.value)
  const handleMcpServersChange = (event) => setSelectedMcpServers(event.target.value)
  const handleStepTypesChange = (event) => setSelectedStepTypes(event.target.value)
  const handleOrchestratorPathChange = (event) => setSelectedOrchestratorPath(event.target.value)

  const buildFetchPayload = () => ({
    used_index: selectedIndexes,
    feedback: selectedFeedback,
    date_range: dates,
    text_search: textSearch,
    tenants: selectedTenant,
    language: selectedLanguage,
    skills: selectedSkills,
    tools: selectedTools,
    mcp_servers: selectedMcpServers,
    step_types: selectedStepTypes,
    orchestrator_path: selectedOrchestratorPath,
    eval_data: {
      answer_relevancy: answerRelevancyRange,
      faithfulness: faithfulnessRange
    }
  })

  const handleFetchData = async () => {
    setError(null)
    setWarning(null)
    setLoading(true)
    try {
      const response = await window.PDR.api.post(
        `${getEnv('REACT_APP_AI_INSIGHTS_BASE_URL')}/analyze_external_v2`,
        { ...buildFetchPayload(), analyze: false }
      )
      setSourcesCount(response?.sources_count || {})
      if (response?.note != null) {
        setWarning(response.note)
      }

      const mapped = (response?.data || []).map(mapChatTurnToPoint)
      setPoints(mapped)
      setMetrics(response?.metrics || null)
    } catch (err) {
      console.error('Error fetching data:', err)
      setWarning(null)
      setError('No documents found for these filters, or a server error occurred.')
    }
    setLoading(false)
  }

  const loadingFunction = () => {
    return <LoadingIndicator />
  }

  const renderContent = () => {
    if (points.length === 0) {
      return null
    }

    return (
      <>
        {warning && <div className="warning">{warning}</div>}
        <div className="middle-part">
          <div className="left-part">
            <QuestionsTableSlim
              points={points}
              onRowClick={handleFullConversation}
            />
          </div>

          <div className="right-part">
            <SourcesTable data={sourcesCount} />
            <MetricsDisplay metrics={metrics} />
          </div>
        </div>
      </>
    )
  }

  // add debug UI state
  const [showDebug, setShowDebug] = useState(false)
  const [debugCommand, setDebugCommand] = useState('')

  // on Enter send GET to /debug/<cmd>
  const handleDebugInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      window.PDR.api
        .get(`${getEnv('REACT_APP_AI_INSIGHTS_BASE_URL')}/debug/${debugCommand}`)
        .then((r) => console.log('Debug response:', r.data))
        .catch((err) => console.error('Debug error:', err))
      setDebugCommand('')
      setShowDebug(false)
    }
  }

  // Fetch approved queries data + filter options on component mount
  useEffect(() => {
    const fetchApprovedQueriesData = async () => {
      try {
        const response = await window.PDR.api.get(
          `${getEnv('REACT_APP_AI_INSIGHTS_BASE_URL')}/approved_queries_v2`
        )
        setApprovedQueriesData(response)
      } catch (error) {
        console.error('Error fetching approved queries data:', error)
      }
    }

    const fetchFilterOptions = async () => {
      try {
        const response = await window.PDR.api.get(
          `${getEnv('REACT_APP_AI_INSIGHTS_BASE_URL')}/chat_turns/filter_options`
        )
        setFilterOptions({
          skills: response?.skills || [],
          tools: response?.tools || [],
          mcp_servers: response?.mcp_servers || [],
          indexes: response?.indexes || [],
          languages: response?.languages || [],
          step_types: response?.step_types || ['reasoning', 'tool', 'rag', 'skill', 'mcp', 'sub_agent', 'approval'],
          orchestrator_paths: response?.orchestrator_paths || ['new', 'legacy']
        })
      } catch (error) {
        console.error('Error fetching filter options:', error)
      }
    }

    fetchApprovedQueriesData()
    fetchFilterOptions()
  }, [])

  // Transform approved queries data for the chart
  const transformApprovedQueriesData = useMemo(() => {
    if (!approvedQueriesData || approvedQueriesData.length === 0) return { keys: [], series: [] }

    const keys = approvedQueriesData.map((item) => `${item.week}      ${item.total} Queries`)

    const series = [
      {
        type: 'line',
        name: 'Approved (%)',
        color: '#2ECC71',
        data: approvedQueriesData.map((item, index) => ({
          key: keys[index],
          value: item.approved_percentage
        }))
      },
      {
        type: 'line',
        name: 'Answer Relevancy (%)',
        color: '#E74C3C',
        data: approvedQueriesData.map((item, index) => ({
          key: keys[index],
          value: item.answer_relevancy_percentage
        }))
      },
      {
        type: 'line',
        name: 'Faithfulness (%)',
        color: '#F39C12',
        data: approvedQueriesData.map((item, index) => ({
          key: keys[index],
          value: item.faithfulness_percentage
        }))
      }
    ]

    return { keys, series }
  }, [approvedQueriesData])

  return (
    // make parent relative to contain absolute trigger
    <div style={{ position: 'relative' }}>
      {/* debug hover‐trigger in top‐right */}
      <div
        style={{ position: 'absolute', top: 0, right: 0, width: 30, height: 30 }}
        onMouseEnter={() => setShowDebug(true)}
        onMouseLeave={() => setShowDebug(false)}>
        {showDebug && (
          <input
            type="text"
            value={debugCommand}
            onChange={(e) => setDebugCommand(e.target.value)}
            onKeyDown={handleDebugInputKeyDown}
            placeholder="debug"
            style={{ position: 'absolute', top: 0, right: 0, zIndex: 1000 }}
          />
        )}
      </div>

      <BottomRightButton />
      <div className="header">
        {/* Icon and title */}
        <img
          className="stratsys-search-logo"
          src="https://frontend-packages.svc.stratsys.com/logos/icon-dark.svg"
          alt="Stratsys Icon"
        />
        <div className="one-ux-typography--heading-500">
          <one-ux-text>Query Analysis</one-ux-text>
        </div>
      </div>

      <div className="filters">
        <one-ux-field label="Indexes" width="1/12">
          <one-ux-dropdown placeholder={'Value'}>
            <one-ux-list
              multiple
              options={filterOptions.indexes.map((i) => ({ value: i, text: i }))}
              value={selectedIndexes}
              onInput={handleIndexChange}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Feedback" width="1/12">
          <one-ux-dropdown placeholder={'Value'}>
            <one-ux-list
              multiple
              options={feedback_dict}
              value={selectedFeedback}
              onInput={handleFeedbackChange}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Tenant" width="1/12">
          <one-ux-dropdown placeholder={'Value'}>
            <div slot="preview">{tenant_preview}</div>
            <one-ux-search value={tenant_search_value} onInput={handleTenantSearchChange} width="max"></one-ux-search>
            <one-ux-list
              multiple
              options={tenant_dict.filter((x) =>
                (x.text || '').toLowerCase().startsWith((tenant_search_value || '').toLowerCase())
              )}
              value={selectedTenant}
              onInput={handleTenantChange}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label={'Text Search'} width="1/12">
          <one-ux-input
            width="1/12"
            onChange={handleTextSearchChange}
            value={textSearch}
            placeholder="Text search"></one-ux-input>
        </one-ux-field>

        <one-ux-field label="Language" width="1/12">
          <one-ux-dropdown placeholder="Value">
            <one-ux-list
              options={
                filterOptions.languages.length
                  ? filterOptions.languages.map((l) => ({ value: l, text: l }))
                  : [
                      { value: 'sv', text: 'Swedish' },
                      { value: 'no', text: 'Norwegian' },
                      { value: 'en', text: 'English' }
                    ]
              }
              value={selectedLanguage}
              onInput={(e) => setSelectedLanguage(e.target.value)}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Orchestrator" width="1/12">
          <one-ux-dropdown placeholder="new">
            <one-ux-list
              options={filterOptions.orchestrator_paths.map((p) => ({ value: p, text: p }))}
              value={selectedOrchestratorPath}
              onInput={handleOrchestratorPathChange}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Skills" width="1/12">
          <one-ux-dropdown placeholder="Select skills">
            <one-ux-list
              multiple
              options={filterOptions.skills.map((s) => ({ value: s, text: s }))}
              value={selectedSkills}
              onInput={handleSkillsChange}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Tools" width="1/12">
          <one-ux-dropdown placeholder="Select tools">
            <one-ux-list
              multiple
              options={filterOptions.tools.map((t) => ({ value: t, text: t }))}
              value={selectedTools}
              onInput={handleToolsChange}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="MCP Servers" width="1/12">
          <one-ux-dropdown placeholder="Select servers">
            <one-ux-list
              multiple
              options={filterOptions.mcp_servers.map((s) => ({ value: s, text: s }))}
              value={selectedMcpServers}
              onInput={handleMcpServersChange}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Step Type" width="1/12">
          <one-ux-dropdown placeholder="Select step types">
            <one-ux-list
              multiple
              options={filterOptions.step_types.map((t) => ({ value: t, text: t }))}
              value={selectedStepTypes}
              onInput={handleStepTypesChange}></one-ux-list>
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Date range">
          <one-ux-dropdown placeholder="Filter">
            <one-ux-date-picker range="True" value={dates} onChange={handleChangeDate}></one-ux-date-picker>
          </one-ux-dropdown>
        </one-ux-field>
      </div>
      <div className="filter-buttons">
        <one-ux-slider
          range
          label="Answer Relevancy"
          min="0"
          max="1"
          steps={deepEvalSliderValues}
          value={answerRelevancyRange}
          onInput={handleRelevancyChange}
          implicit={true}></one-ux-slider>

        <one-ux-slider
          range
          label="Faithfulness"
          min="0"
          max="1"
          steps={deepEvalSliderValues}
          value={faithfulnessRange}
          onInput={handleFaithfulnessChange}
          implicit={true}></one-ux-slider>

        <one-ux-button
          class="start-button"
          label="Fetch"
          purpose="main"
          weight="high"
          state="default"
          onClick={handleFetchData}></one-ux-button>
      </div>

      {loading ? loadingFunction() : error ? renderError() : renderContent()}
      {/* Add metrics chart */}
      {approvedQueriesData.length > 0 && (
        <div className="approved-queries-chart">
          <h2 className="chart-title">Query Performance Metrics by Week</h2>
          <one-ux-plot-chart
            style={{ width: '100%', height: '300px' }}
            axis-left-range={{ min: '50', max: '100' }}
            keys={transformApprovedQueriesData.keys}
            series={transformApprovedQueriesData.series}></one-ux-plot-chart>
        </div>
      )}
      {conversationData && (
        <ConversationPopup conversation={conversationData} onClose={closePopup} metadata={conversationMetaData} />
      )}
    </div>
  )
}

export default QueryAnalysis
